import { MessageSquareTextIcon } from "lucide-react";
import { useState } from "react";
import type { CmmdProjectLeadBriefing } from "@cmmd-forge/contracts";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";

export interface ProjectLeadNeedsYouProps {
  readonly briefing: CmmdProjectLeadBriefing;
  readonly onDecideApproval: (
    approvalId: string,
    decision: "approved" | "denied",
    reason: string,
  ) => Promise<void>;
}

export function ProjectLeadNeedsYou({ briefing, onDecideApproval }: ProjectLeadNeedsYouProps) {
  const [selectedApprovalId, setSelectedApprovalId] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const pendingApprovals = briefing.approvalRequests.filter(
    (approval) => approval.status === "pending",
  );

  const submitDecision = (approvalId: string, decision: "approved" | "denied"): void => {
    setSubmitting(true);
    void onDecideApproval(approvalId, decision, reason.trim())
      .then(() => {
        setSelectedApprovalId(null);
        setReason("");
      })
      .catch(() => {
        // The sheet owns the visible mutation error; preserve the rationale for a retry.
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <section
      aria-labelledby="lead-briefing-approvals-heading"
      className="space-y-3 border-t border-border pt-5"
    >
      <div className="flex items-center gap-2">
        <MessageSquareTextIcon className="size-4 text-muted-foreground" aria-hidden />
        <h3 id="lead-briefing-approvals-heading" className="text-sm font-semibold text-foreground">
          Needs you
        </h3>
      </div>
      {pendingApprovals.length > 0 ? (
        <div className="space-y-2">
          {pendingApprovals.slice(0, 3).map((approval) => (
            <div
              key={approval.approvalId}
              className="space-y-2 border-b border-border pb-2 last:border-b-0 last:pb-0"
            >
              <p className="text-xs font-medium text-foreground">{approval.summary}</p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                {approval.action.replaceAll("_", " ")} · requested by Agent Member{" "}
                {approval.requestedByAgentSeatId}
              </p>
              {selectedApprovalId === approval.approvalId ? (
                <div className="space-y-2">
                  <Textarea
                    aria-label={`Reason for ${approval.summary}`}
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    placeholder="Why are you approving or denying this?"
                    size="sm"
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      disabled={submitting || reason.trim().length === 0}
                      onClick={() => submitDecision(approval.approvalId, "approved")}
                    >
                      {submitting ? "Saving…" : "Approve"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={submitting || reason.trim().length === 0}
                      onClick={() => submitDecision(approval.approvalId, "denied")}
                    >
                      Deny
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setSelectedApprovalId(approval.approvalId)}
                >
                  Review
                </Button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs leading-5 text-muted-foreground">
          No Project Lead approvals need a human decision.
        </p>
      )}
    </section>
  );
}
